import React from 'react';
import {
    Table,
    Button,
    Card,
    Space,
    message,
    Modal,
    Drawer,
    Input,
    Popconfirm,
    Empty,
    Pagination,
    Spin,
    Image,
} from 'antd';
import {
    ReloadOutlined,
    SearchOutlined,
    FolderOpenOutlined,
    UserAddOutlined,
} from '@ant-design/icons';
import {
    listImages,
    scanImages,
    deleteImage,
    assignImageAccess,
    revokeImageAccess,
    listImageAccessUsers,
    getImageViewUrl,
} from '../../api/image';
import { listUsers } from '../../api/sys_user';
import { getUser } from '../../utils/auth';
import {
    ROLES,
    displayRoleName,
    canScanImages,
    canAssignImage,
    canDeleteImage,
    canViewImageAccess,
    isSuperAdmin,
    isAdmin,
} from '../../utils/role';
import ImageCard from './ImageCard';
import ScanPathPicker from '../ScanPathPicker/ScanPathPicker';
import MediaAccessUserPicker from '../MediaAccess/MediaAccessUserPicker';
import { isMacOsSidecarFileName } from '../../utils/format';
import { drawerWidth } from '../../utils/responsive';
import './ImageBrowser.css';

const DEFAULT_PAGE_SIZE = 24;

class ImageBrowser extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            images: [],
            loading: false,
            keyword: '',
            page: 1,
            pageSize: DEFAULT_PAGE_SIZE,
            scanOpen: false,
            scanning: false,
            accessOpen: false,
            accessImage: null,
            accessUsers: [],
            accessLoading: false,
            assignPickerOpen: false,
            assignableUsers: [],
            assignableLoading: false,
            assigning: false,
            previewOpen: false,
            previewImage: null,
        };
    }

    componentDidMount() {
        this.listAbort = new AbortController();
        this.loadImages();
    }

    componentWillUnmount() {
        this.listAbort?.abort();
        if (this.refreshTimer) {
            clearTimeout(this.refreshTimer);
        }
    }

    scheduleListRefresh = () => {
        if (this.refreshTimer) {
            clearTimeout(this.refreshTimer);
        }
        this.refreshTimer = setTimeout(() => this.loadImages(), 600);
    };

    handleMediaVerified = () => {
        this.scheduleListRefresh();
    };

    componentDidUpdate(prevProps, prevState) {
        if (prevState.keyword !== this.state.keyword) {
            this.setState({ page: 1 });
        }
        const filtered = this.getFilteredImages();
        const maxPage = Math.max(1, Math.ceil(filtered.length / this.state.pageSize) || 1);
        if (this.state.page > maxPage) {
            this.setState({ page: maxPage });
        }
    }

    loadImages = async () => {
        this.listAbort?.abort();
        this.listAbort = new AbortController();
        const { signal } = this.listAbort;
        this.setState({ loading: true });
        try {
            const list = await listImages({ signal });
            const sidecars = list.filter((item) => isMacOsSidecarFileName(item.fileName));
            const displayList = list.filter((item) => !isMacOsSidecarFileName(item.fileName));
            if (sidecars.length > 0) {
                message.info(
                    `已隐藏 ${sidecars.length} 个 macOS 系统伴生文件（._ 开头），重新扫描后可从库中清除`
                );
            }
            this.setState({ images: displayList });
        } catch (e) {
            if (e?.name === 'AbortError') {
                return;
            }
            message.error({
                key: 'image-list-load',
                content: e.message || '加载图片列表失败',
            });
            this.setState({ images: [] });
        } finally {
            if (!signal.aborted) {
                this.setState({ loading: false });
            }
        }
    };

    getFilteredImages = () => {
        const { images, keyword } = this.state;
        const kw = keyword.trim().toLowerCase();
        if (!kw) return images;
        return images.filter(
            (v) =>
                (v.fileName && v.fileName.toLowerCase().includes(kw)) ||
                (v.fileExt && v.fileExt.toLowerCase().includes(kw)) ||
                (v.filePath && v.filePath.toLowerCase().includes(kw))
        );
    };

    getPaginatedImages = () => {
        const filtered = this.getFilteredImages();
        const { page, pageSize } = this.state;
        const start = (page - 1) * pageSize;
        return filtered.slice(start, start + pageSize);
    };

    loadAssignableUsers = async () => {
        const currentUser = getUser();
        this.setState({ assignableLoading: true });
        try {
            const all = await listUsers();
            const list = all.filter((u) => {
                if (u.status !== '0') return false;
                if (isAdmin(currentUser)) return u.roleKey === ROLES.USER;
                return u.roleKey === ROLES.USER || u.roleKey === ROLES.ADMIN;
            });
            this.setState({ assignableUsers: list });
            return list;
        } catch {
            this.setState({ assignableUsers: [] });
            return [];
        } finally {
            this.setState({ assignableLoading: false });
        }
    };

    handleScan = async (path) => {
        const trimmed = path.trim();
        if (!trimmed) {
            message.warning('请选择扫描路径');
            return;
        }
        this.setState({ scanning: true });
        try {
            const result = await scanImages(trimmed);
            message.success(
                `扫描完成：新增 ${result.inserted ?? 0}，移除 ${result.removed ?? 0}，过小 ${result.tooSmall ?? 0}`
            );
            this.setState({ scanOpen: false });
            await this.loadImages();
        } catch (e) {
            message.error(e.message || '扫描失败');
        } finally {
            this.setState({ scanning: false });
        }
    };

    openAccessDrawer = async (record) => {
        this.setState({
            accessImage: record,
            accessOpen: true,
            assignPickerOpen: false,
            accessLoading: true,
        });
        await this.loadAssignableUsers();
        try {
            const users = await listImageAccessUsers(record.id);
            this.setState({ accessUsers: Array.isArray(users) ? users : [] });
        } catch (e) {
            message.error(e.message || '加载分配列表失败');
            this.setState({ accessUsers: [] });
        } finally {
            this.setState({ accessLoading: false });
        }
    };

    openAssignPicker = async () => {
        await this.loadAssignableUsers();
        this.setState({ assignPickerOpen: true });
    };

    handleBatchAssign = async (userIds) => {
        const { accessImage } = this.state;
        if (!accessImage || userIds.length === 0) {
            return;
        }
        this.setState({ assigning: true });
        try {
            const results = await Promise.allSettled(
                userIds.map((userId) => assignImageAccess(accessImage.id, userId))
            );
            const ok = results.filter((r) => r.status === 'fulfilled').length;
            const fail = results.length - ok;
            if (ok > 0) {
                message.success(`已成功分配 ${ok} 个用户`);
            }
            if (fail > 0) {
                message.warning(`${fail} 个用户分配失败（可能已分配或无权限）`);
            }
            this.setState({ assignPickerOpen: false });
            const users = await listImageAccessUsers(accessImage.id);
            this.setState({ accessUsers: users });
        } catch (e) {
            message.error(e.message || '分配失败');
        } finally {
            this.setState({ assigning: false });
        }
    };

    handleRevoke = async (userId) => {
        const { accessImage } = this.state;
        try {
            await revokeImageAccess(accessImage.id, userId);
            message.success('已撤销浏览权限');
            const users = await listImageAccessUsers(accessImage.id);
            this.setState({ accessUsers: users });
        } catch (e) {
            message.error(e.message || '撤销失败');
        }
    };

    handleView = (record) => {
        this.setState({ previewImage: record, previewOpen: true });
    };

    handleDelete = async (record) => {
        try {
            await deleteImage(record.id);
            message.success('已删除图片记录');
            await this.loadImages();
        } catch (e) {
            message.error(e.message || '删除失败');
        }
    };

    confirmDelete = (record) => {
        Modal.confirm({
            title: '确认删除该图片记录？',
            content: '仅删除库内记录，不删除磁盘上的图片文件。',
            okText: '删除',
            okType: 'danger',
            cancelText: '取消',
            onOk: () => this.handleDelete(record),
        });
    };

    render() {
        const currentUser = getUser();
        const {
            loading,
            page,
            pageSize,
            scanOpen,
            scanning,
            accessOpen,
            accessImage,
            accessUsers,
            accessLoading,
            assignPickerOpen,
            assignableUsers,
            assignableLoading,
            assigning,
            previewOpen,
            previewImage,
        } = this.state;

        const filteredImages = this.getFilteredImages();
        const paginatedImages = this.getPaginatedImages();

        const roleHint = isSuperAdmin(currentUser)
            ? '超级管理员：可查看全部图片、扫描入库、分配浏览'
            : isAdmin(currentUser)
              ? '管理员：可查看全部图片、向普通用户分配浏览'
              : '普通用户：仅可浏览已分配的图片';

        const emptyDescription = canScanImages(currentUser)
            ? '暂无图片，请先扫描入库'
            : '暂无可浏览的图片';

        return (
            <div className="image-browser">
                <Card className="image-browser__card" bordered={false}>
                    <div className="image-browser__header">
                        <div>
                            <h2 className="image-browser__title">图片浏览</h2>
                            <p className="image-browser__subtitle">{roleHint}</p>
                        </div>
                        <Space wrap>
                            <Input.Search
                                placeholder="搜索图片名称"
                                allowClear
                                style={{ width: 280 }}
                                enterButton={<SearchOutlined />}
                                onSearch={(v) => this.setState({ keyword: v })}
                                onChange={(e) => {
                                    if (!e.target.value) this.setState({ keyword: '' });
                                }}
                            />
                            <Button icon={<ReloadOutlined />} onClick={this.loadImages}>
                                刷新
                            </Button>
                            {canScanImages(currentUser) && (
                                <Button
                                    type="primary"
                                    icon={<FolderOpenOutlined />}
                                    onClick={() => this.setState({ scanOpen: true })}
                                >
                                    扫描入库
                                </Button>
                            )}
                        </Space>
                    </div>

                    <Spin spinning={loading}>
                        {filteredImages.length === 0 && !loading ? (
                            <Empty className="image-browser__empty" description={emptyDescription} />
                        ) : (
                            <>
                                <p className="image-browser__count">共 {filteredImages.length} 张图片</p>
                                <div className="image-grid">
                                    {paginatedImages.map((image) => (
                                        <ImageCard
                                            key={image.id}
                                            image={image}
                                            onView={this.handleView}
                                            onAssign={this.openAccessDrawer}
                                            onDelete={this.confirmDelete}
                                            onMediaVerified={this.handleMediaVerified}
                                            showAssign={canViewImageAccess(currentUser)}
                                            showDelete={canDeleteImage(currentUser)}
                                        />
                                    ))}
                                </div>
                                {filteredImages.length > 0 && (
                                    <div className="image-browser__pagination">
                                        <Pagination
                                            current={page}
                                            pageSize={pageSize}
                                            total={filteredImages.length}
                                            showSizeChanger
                                            showTotal={(t) => `共 ${t} 张`}
                                            pageSizeOptions={['12', '24', '48', '96']}
                                            onChange={(p, size) => {
                                                this.setState({ page: p, pageSize: size });
                                            }}
                                        />
                                    </div>
                                )}
                            </>
                        )}
                    </Spin>
                </Card>

                <ScanPathPicker
                    open={scanOpen}
                    mediaType="image"
                    title="扫描图片入库"
                    hint="在服务端 image-scan-roots 白名单内浏览目录，选择要扫描的文件夹或单个图片文件"
                    scanning={scanning}
                    onCancel={() => this.setState({ scanOpen: false })}
                    onScan={this.handleScan}
                />

                <Drawer
                    title={accessImage ? `浏览权限：${accessImage.fileName}` : '浏览权限'}
                    width={drawerWidth(480)}
                    open={accessOpen}
                    onClose={() => this.setState({ accessOpen: false, accessImage: null })}
                    destroyOnClose
                >
                    {canAssignImage(currentUser) && (
                        <Button
                            type="primary"
                            icon={<UserAddOutlined />}
                            onClick={this.openAssignPicker}
                            style={{ marginBottom: 16 }}
                        >
                            选择用户分配
                        </Button>
                    )}
                    <Table
                        size="small"
                        rowKey="userId"
                        loading={accessLoading}
                        scroll={{ x: 'max-content' }}
                        dataSource={accessUsers}
                        pagination={false}
                        columns={[
                            { title: '用户', dataIndex: 'loginId' },
                            {
                                title: '角色',
                                dataIndex: 'roleKey',
                                render: (k) => displayRoleName({ roleKey: k }),
                            },
                            {
                                title: '操作',
                                key: 'op',
                                render: (_, row) => (
                                    <Popconfirm
                                        title="确认撤销？"
                                        onConfirm={() => this.handleRevoke(row.userId)}
                                    >
                                        <Button type="link" size="small" danger>
                                            撤销
                                        </Button>
                                    </Popconfirm>
                                ),
                            },
                        ]}
                        locale={{ emptyText: '暂未分配给任何用户' }}
                    />
                </Drawer>

                <MediaAccessUserPicker
                    open={assignPickerOpen}
                    title="选择用户分配浏览权限"
                    resourceLabel={accessImage?.fileName}
                    users={assignableUsers}
                    assignedUserIds={accessUsers.map((u) => u.userId)}
                    loading={assignableLoading}
                    confirming={assigning}
                    onCancel={() => this.setState({ assignPickerOpen: false })}
                    onConfirm={this.handleBatchAssign}
                />

                <Modal
                    className="image-preview-modal"
                    title={previewImage?.fileName}
                    open={previewOpen}
                    onCancel={() => this.setState({ previewOpen: false, previewImage: null })}
                    footer={null}
                    width="auto"
                    centered
                    destroyOnClose
                >
                    {previewImage && (
                        <Image
                            src={getImageViewUrl(previewImage.id)}
                            alt={previewImage.fileName}
                            preview={{ mask: '放大' }}
                            style={{ maxWidth: '90vw' }}
                            onLoad={() => {
                                if (previewImage.status === '1') {
                                    this.scheduleListRefresh();
                                }
                            }}
                        />
                    )}
                </Modal>
            </div>
        );
    }
}

export default ImageBrowser;
